import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { AddressInput } from '../components/AddressInput';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Lock, Mail, Loader2, CheckCircle, Receipt, ArrowRight, Building2, ShieldCheck, ImageUp, Move, ZoomIn, RotateCcw, Crop, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { validatePersonFullName } from '../lib/personNames';
import { COMPANY_YEAR_END_OPTIONS, normaliseYearEnd } from '../lib/companyYearEnd';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PROFILE_TABS = new Set(['profile', 'security', 'email']);
const RECEIPT_LOGO_ASPECT = 22 / 7;
const RECEIPT_LOGO_PREVIEW_WIDTH = 352;
const RECEIPT_LOGO_PREVIEW_HEIGHT = Math.round(RECEIPT_LOGO_PREVIEW_WIDTH / RECEIPT_LOGO_ASPECT);
const RECEIPT_LOGO_EXPORT_WIDTH = 1100;
const RECEIPT_LOGO_EXPORT_HEIGHT = 350;

const scrollToSectionWithOffset = (element) => {
  if (!element || typeof window === 'undefined') return;
  const top = element.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: 'smooth' });
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resolveAssetUrl = (path) => {
  if (typeof path !== 'string') return '';
  const normalizedPath = path.trim();
  if (!normalizedPath) return '';
  if (['none', 'null', 'undefined'].includes(normalizedPath.toLowerCase())) return '';
  if (!/^https?:\/\//i.test(normalizedPath) && !/^\/?uploads\//i.test(normalizedPath)) return '';
  if (/^(https?:\/\/|blob:|data:)/i.test(normalizedPath)) return normalizedPath;
  if (normalizedPath.startsWith('/')) return `${API_URL}${normalizedPath}`;
  return `${API_URL}/${normalizedPath.replace(/^\/+/, '')}`;
};

const withCacheBuster = (url, seed) => {
  if (!url) return '';
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(seed)}`;
};

const cropImageToReceiptLogo = async ({ imageSrc, offsetX, offsetY, previewWidth, previewHeight, displayWidth, displayHeight }) => {
  const canvas = document.createElement('canvas');
  canvas.width = RECEIPT_LOGO_EXPORT_WIDTH;
  canvas.height = RECEIPT_LOGO_EXPORT_HEIGHT;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas is not supported in this browser.');
  }

  const image = new Image();
  image.src = imageSrc;

  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error('Failed to load the selected image.'));
  });

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);

  const exportScale = canvas.width / previewWidth;
  const drawX = ((previewWidth - displayWidth) / 2 + offsetX) * exportScale;
  const drawY = ((previewHeight - displayHeight) / 2 + offsetY) * exportScale;
  const drawWidth = displayWidth * exportScale;
  const drawHeight = displayHeight * exportScale;

  context.drawImage(
    image,
    drawX,
    drawY,
    drawWidth,
    drawHeight
  );

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((result) => {
      if (!result) {
        reject(new Error('Failed to prepare the cropped logo.'));
        return;
      }
      resolve(result);
    }, 'image/png');
  });

  return new File([blob], 'receipt-logo.png', { type: 'image/png' });
};

export const Profile = () => {
  useEffect(() => { document.title = 'My Profile - SIYAK'; }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, logout } = useAuth();
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 390 : window.innerWidth));
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [logoImageFailed, setLogoImageFailed] = useState(false);
  const [logoCacheSeed, setLogoCacheSeed] = useState(() => Date.now().toString());
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropSourceUrl, setCropSourceUrl] = useState(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropImageMeta, setCropImageMeta] = useState(null);
  const [cropPreparing, setCropPreparing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const cropDragRef = useRef({ pointerId: null, startX: 0, startY: 0, originX: 0, originY: 0 });
  const logoInputRef = useRef(null);
  
  const [profileForm, setProfileForm] = useState({
    name: '',
    phone: '',
    company_name: '',
    business_address: '',
    vat_number: '',
    company_registration: '',
    website: '',
    company_year_end: '',
    director_name: '',
  });
  const [profileFieldErrors, setProfileFieldErrors] = useState({});
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [emailForm, setEmailForm] = useState({ new_email: '' });
  const [emailSent, setEmailSent] = useState(false);
  const [closeAccountDialogOpen, setCloseAccountDialogOpen] = useState(false);
  const [closeAccountLoading, setCloseAccountLoading] = useState(false);

  const isLandlord = user?.role === 'landlord';
  const isNonLandlord = user?.role !== 'landlord';
  const isCompanyLandlord = isLandlord && user?.ownership_type === 'company';
  const savedLogoUrl = resolveAssetUrl(user?.logo_url);
  const hasUsableLogo = Boolean(logoFile || logoPreviewUrl || (savedLogoUrl && !logoImageFailed));
  const nonLandlordProfileItems = [
    { label: 'Full Name', complete: Boolean(profileForm.name?.trim()), required: true },
    { label: 'Login email', complete: Boolean(user?.email?.trim()), required: true },
    { label: 'Phone number', complete: Boolean(profileForm.phone?.trim()) },
  ];
  const nonLandlordMissingItems = nonLandlordProfileItems.filter((item) => !item.complete);
  const nonLandlordCompleteCount = nonLandlordProfileItems.filter((item) => item.complete).length;
  const nonLandlordTotalCount = nonLandlordProfileItems.length;
  const nonLandlordPercent = Math.round((nonLandlordCompleteCount / nonLandlordTotalCount) * 100);
  const receiptSetupItems = isCompanyLandlord
    ? [
      { label: 'Company name', complete: Boolean(profileForm.company_name?.trim()), required: true },
      { label: 'Login email', complete: Boolean(user?.email?.trim()), required: true },
      { label: 'Primary contact Full Name', complete: Boolean(profileForm.name?.trim()) },
      { label: 'Business address', complete: Boolean(profileForm.business_address?.trim()) },
      { label: 'Phone or website', complete: Boolean(profileForm.phone?.trim() || profileForm.website?.trim()) },
      { label: 'Logo', complete: hasUsableLogo },
    ]
    : [
      { label: 'Landlord Full Name', complete: Boolean(profileForm.name?.trim()), required: true },
      { label: 'Login email', complete: Boolean(user?.email?.trim()), required: true },
      { label: 'Correspondence address', complete: Boolean(profileForm.business_address?.trim()) },
      { label: 'Phone or website', complete: Boolean(profileForm.phone?.trim() || profileForm.website?.trim()) },
      { label: 'Logo', complete: hasUsableLogo },
    ];
  const receiptMissingItems = receiptSetupItems.filter((item) => !item.complete);
  const receiptSetupCompleteCount = receiptSetupItems.filter((item) => item.complete).length;
  const receiptSetupTotalCount = receiptSetupItems.length;
  const highlightedSection = new URLSearchParams(location.search).get('section');
  const receiptSectionHighlighted = highlightedSection === 'receipt-settings';
  const receiptSetupPercent = Math.round((receiptSetupCompleteCount / receiptSetupTotalCount) * 100);
  const getSectionHighlightClass = (section) => (
    highlightedSection === section ? 'ring-2 ring-emerald-400 ring-offset-2' : ''
  );
  const selectedLogoPreviewUrl = useMemo(() => {
    if (!logoFile) return null;
    return URL.createObjectURL(logoFile);
  }, [logoFile]);
  const persistedLogoUrl = useMemo(
    () => withCacheBuster(savedLogoUrl, logoCacheSeed),
    [logoCacheSeed, savedLogoUrl]
  );
  const currentLogoUrl = selectedLogoPreviewUrl || logoPreviewUrl || persistedLogoUrl;
  const cropScale = useMemo(() => {
    if (!cropImageMeta?.width || !cropImageMeta?.height) return 1;
    const cropPreviewWidth = Math.max(220, Math.min(RECEIPT_LOGO_PREVIEW_WIDTH, viewportWidth - 96));
    const cropPreviewHeight = Math.round(cropPreviewWidth / RECEIPT_LOGO_ASPECT);
    const baseScale = Math.min(
      cropPreviewWidth / cropImageMeta.width,
      cropPreviewHeight / cropImageMeta.height
    );
    return baseScale * cropZoom;
  }, [cropImageMeta, cropZoom, viewportWidth]);
  const cropPreviewWidth = useMemo(
    () => Math.max(220, Math.min(RECEIPT_LOGO_PREVIEW_WIDTH, viewportWidth - 96)),
    [viewportWidth]
  );
  const cropPreviewHeight = useMemo(
    () => Math.round(cropPreviewWidth / RECEIPT_LOGO_ASPECT),
    [cropPreviewWidth]
  );
  const cropDisplaySize = useMemo(() => {
    if (!cropImageMeta?.width || !cropImageMeta?.height) {
      return { width: cropPreviewWidth, height: cropPreviewHeight };
    }
    return {
      width: cropImageMeta.width * cropScale,
      height: cropImageMeta.height * cropScale,
    };
  }, [cropImageMeta, cropPreviewHeight, cropPreviewWidth, cropScale]);
  useEffect(() => () => {
    if (selectedLogoPreviewUrl) {
      URL.revokeObjectURL(selectedLogoPreviewUrl);
    }
  }, [selectedLogoPreviewUrl]);

  useEffect(() => () => {
    if (cropSourceUrl) {
      URL.revokeObjectURL(cropSourceUrl);
    }
  }, [cropSourceUrl]);

  useEffect(() => {
    setLogoImageFailed(false);
  }, [currentLogoUrl]);

  useEffect(() => {
    if (!savedLogoUrl) return;
    setLogoCacheSeed(Date.now().toString());
  }, [savedLogoUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        phone: user.phone || '',
        company_name: user.company_name || '',
        business_address: user.business_address || '',
        vat_number: user.vat_number || '',
        company_registration: user.company_registration || '',
        website: user.website || '',
        company_year_end: user.company_year_end || '',
        director_name: user.director_name || '',
      });
    }
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    setActiveTab(PROFILE_TABS.has(tab) ? tab : 'profile');
  }, [location.search]);

  useEffect(() => {
    if (activeTab !== 'profile') return;
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (!section) return;

    const sectionTargetMap = {
      'receipt-settings': 'receipt-settings-section',
      'contact-branding': 'contact-branding-section',
      'personal-details': 'personal-details-section',
      'contact-details': 'contact-details-section',
    };
    const targetId = sectionTargetMap[section];
    if (!targetId) return;
    if (section === 'receipt-settings' && !isLandlord) return;

    const timer = window.setTimeout(() => {
      const target = document.getElementById(targetId);
      scrollToSectionWithOffset(target);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [activeTab, isLandlord, location.search]);

  useEffect(() => {
    const handleFocus = () => {
      if (refreshUser) {
        refreshUser();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocus();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshUser]);

  const uploadLogoFile = async (fileToUpload) => {
    const formData = new FormData();
    formData.append('file', fileToUpload);
    const response = await axios.post(`${API_URL}/api/users/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (response?.data?.logo_url) {
      const nextSeed = Date.now().toString();
      setLogoCacheSeed(nextSeed);
      setLogoPreviewUrl(withCacheBuster(resolveAssetUrl(response.data.logo_url), nextSeed));
      setLogoImageFailed(false);
    }

    return response?.data;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const fullName = validatePersonFullName(profileForm.name, isCompanyLandlord ? 'Primary contact Full Name' : 'Landlord Full Name');
    if (!fullName.valid) {
      toast.error(fullName.error);
      return;
    }
    if (isCompanyLandlord && !profileForm.company_name?.trim()) {
      setProfileFieldErrors((prev) => ({
        ...prev,
        company_name: 'Company name is required for company ownership.',
      }));
      toast.error('Enter a company name before saving');
      return;
    }
    const directorFullName = profileForm.director_name?.trim()
      ? validatePersonFullName(profileForm.director_name, 'Director or authorised signatory Full Name')
      : null;
    if (directorFullName && !directorFullName.valid) {
      toast.error(directorFullName.error);
      return;
    }

    setProfileFieldErrors({});
    setLoading(true);
    try {
      const payload = {
        name: fullName.value,
        phone: profileForm.phone,
      };

      if (user?.role === 'landlord') {
        payload.business_address = profileForm.business_address;
        payload.website = profileForm.website;

        if (isCompanyLandlord) {
          payload.company_name = profileForm.company_name;
          payload.vat_number = profileForm.vat_number;
          payload.company_registration = profileForm.company_registration;
          payload.company_year_end = profileForm.company_year_end;
          payload.director_name = directorFullName?.value || '';
        } else {
          payload.company_name = null;
          payload.vat_number = null;
          payload.company_registration = null;
          payload.company_year_end = null;
          payload.director_name = null;
        }
      }

      const response = await axios.put(`${API_URL}/api/users/profile`, payload);
      const refreshedUser = refreshUser ? await refreshUser(true) : response?.data;
      if (refreshedUser) {
        setProfileForm({
          name: refreshedUser.name || '',
          phone: refreshedUser.phone || '',
          company_name: refreshedUser.company_name || '',
          business_address: refreshedUser.business_address || '',
          vat_number: refreshedUser.vat_number || '',
          company_registration: refreshedUser.company_registration || '',
          website: refreshedUser.website || '',
          company_year_end: refreshedUser.company_year_end || '',
          director_name: refreshedUser.director_name || '',
        });
      }

      if (logoFile) {
        await uploadLogoFile(logoFile);
        setLogoFile(null);
      }
      toast.success(logoFile ? 'Profile and logo updated successfully' : 'Profile updated successfully');
    } catch (error) {
      if (error.response?.status === 422 && /company name/i.test(error.response?.data?.detail || '')) {
        setProfileFieldErrors((prev) => ({
          ...prev,
          company_name: 'Company name is required for company ownership.',
        }));
      }
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const resetCropState = () => {
    setCropDialogOpen(false);
    setCropZoom(1);
    setCropOffset({ x: 0, y: 0 });
    setCropImageMeta(null);
    setCropPreparing(false);
    setCropSourceUrl((previous) => {
      if (previous) {
        URL.revokeObjectURL(previous);
      }
      return null;
    });
  };

  const handleLogoInputChange = async (e) => {
    const nextFile = e.target.files?.[0] || null;
    e.target.value = '';
    if (!nextFile) return;

    if (!['image/png', 'image/jpeg'].includes(nextFile.type)) {
      toast.error('Only PNG or JPG images are allowed');
      return;
    }

    if (nextFile.size > 2 * 1024 * 1024) {
      toast.error('File too large (max 2MB)');
      return;
    }

    const nextCropSourceUrl = URL.createObjectURL(nextFile);
    const image = new Image();
    image.src = nextCropSourceUrl;

    try {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error('Failed to load the selected image.'));
      });
      setCropSourceUrl((previous) => {
        if (previous) {
          URL.revokeObjectURL(previous);
        }
        return nextCropSourceUrl;
      });
      setCropImageMeta({ width: image.naturalWidth, height: image.naturalHeight });
      setCropZoom(1);
      setCropOffset({ x: 0, y: 0 });
      setCropPreparing(false);
      setCropDialogOpen(true);
    } catch (error) {
      URL.revokeObjectURL(nextCropSourceUrl);
      toast.error(error.message || 'Failed to load the selected image');
    }
  };

  const handleCropPointerDown = (event) => {
    if (!cropSourceUrl) return;
    cropDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: cropOffset.x,
      originY: cropOffset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCropPointerMove = (event) => {
    if (cropDragRef.current.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - cropDragRef.current.startX;
    const deltaY = event.clientY - cropDragRef.current.startY;
    setCropOffset({
      x: cropDragRef.current.originX + deltaX,
      y: cropDragRef.current.originY + deltaY,
    });
  };

  const handleCropPointerUp = (event) => {
    if (cropDragRef.current.pointerId !== event.pointerId) return;
    cropDragRef.current = { pointerId: null, startX: 0, startY: 0, originX: 0, originY: 0 };
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleConfirmCrop = async () => {
    if (!cropSourceUrl || !cropImageMeta?.width || !cropImageMeta?.height) {
      toast.error('Select a logo image first');
      return;
    }

    setCropPreparing(true);
    try {
      const croppedFile = await cropImageToReceiptLogo({
        imageSrc: cropSourceUrl,
        offsetX: cropOffset.x,
        offsetY: cropOffset.y,
        previewWidth: cropPreviewWidth,
        previewHeight: cropPreviewHeight,
        displayWidth: cropDisplaySize.width,
        displayHeight: cropDisplaySize.height,
      });
      setLogoFile(croppedFile);
      setLogoImageFailed(false);
      resetCropState();
    } catch (error) {
      toast.error(error.message || 'Failed to crop the logo');
      setCropPreparing(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/users/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email) {
      toast.error('Please enter a new email address');
      return;
    }
    if (emailForm.new_email === user?.email) {
      toast.error('New email must be different from current email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/users/request-email-change`, {
        new_email: emailForm.new_email
      });
      setEmailSent(true);
      toast.success('Verification email sent to your new address');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request email change');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAccount = async () => {
    setCloseAccountLoading(true);
    try {
      await axios.post(`${API_URL}/api/users/close-account`);
      toast.success('Account closed. Portal access has been removed.');
      setCloseAccountDialogOpen(false);
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(
        (typeof detail === 'object' && detail?.message)
          ? detail.message
          : detail || 'Failed to close account'
      );
    } finally {
      setCloseAccountLoading(false);
    }
  };

  const navigateToProfileView = (tab, section) => {
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (section) {
      params.set('section', section);
    }
    navigate({ pathname: '/profile', search: `?${params.toString()}` }, { replace: true });
  };

  const handleBusinessAddressSelect = (addressObject) => {
    setProfileForm((prev) => ({
      ...prev,
      business_address: Object.prototype.hasOwnProperty.call(addressObject || {}, 'formatted')
        ? (addressObject?.formatted ?? '')
        : prev.business_address,
    }));
  };

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    navigateToProfileView(nextTab, nextTab === 'profile' ? highlightedSection || undefined : undefined);
  };

  const planLabel = `${(user?.plan || 'free').charAt(0).toUpperCase()}${(user?.plan || 'free').slice(1)} Plan`;

  return (
    <Layout>
      <div className="min-h-screen bg-navy-50 py-6 sm:py-8">
        <div className="mx-auto max-w-4xl px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] sm:px-6">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-navy-900">Account Settings</h1>
            <p className="text-muted-foreground mt-1">Manage your profile, rent receipt details, and security settings</p>
          </div>

          <Card className="mb-6 border-navy-200/70 shadow-sm">
            <CardContent className="p-6">
              <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-start">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-navy-900">Account overview</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Keep your account information current so receipts, emails, and account access stay accurate.
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border bg-white p-4">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Login email</p>
                      <p className="mt-1 text-sm font-medium text-navy-900 break-all">{user?.email || 'Not set'}</p>
                    </div>
                    <div className="rounded-lg border bg-white p-4">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Role</p>
                      <p className="mt-1 text-sm font-medium capitalize text-navy-900">{user?.role || 'Unknown'}</p>
                    </div>
                    {isLandlord && (
                      <div className="rounded-lg border bg-white p-4 sm:col-span-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ownership type</p>
                        <p className="mt-1 text-sm font-medium capitalize text-navy-900">
                          {user?.ownership_type === 'company' ? 'Company landlord' : user?.ownership_type === 'individual' ? 'Individual landlord' : 'Not set'}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Current plan</p>
                        <p className="mt-1 text-sm font-medium text-navy-900">{planLabel}</p>
                        <p className="mt-1 text-xs text-muted-foreground">All core features are currently free to use. Optional upgrades may be introduced as the platform grows.</p>
                      </div>
                      {/* // TODO: reinstate when billing is active
                      <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto shrink-0" onClick={() => navigate('/settings/billing')}>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Billing preview
                      </Button> */}
                    </div>
                  </div>
                  <div className={`grid gap-3 ${isLandlord ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                    <div className="rounded-lg border bg-white p-4">
                      <div className="flex items-center gap-2 text-navy-900">
                        <Building2 className="h-4 w-4 text-royal-600" />
                        <p className="text-sm font-medium">Profile basics</p>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {isCompanyLandlord
                          ? (profileForm.company_name?.trim()
                            ? 'Company issuer details are set for receipts.'
                            : 'Add the company name so receipts show the correct issuer.')
                          : (profileForm.name?.trim()
                            ? 'Landlord Full Name added and ready for receipts.'
                            : 'Add your landlord name so receipts show the right issuer.')}
                      </p>
                    </div>
                    {isLandlord ? (
                      <div className="rounded-lg border bg-white p-4">
                        <div className="flex items-center gap-2 text-navy-900">
                          <Receipt className="h-4 w-4 text-emerald-600" />
                          <p className="text-sm font-medium">Receipt readiness</p>
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          {receiptSetupCompleteCount}/{receiptSetupTotalCount} recommended receipt fields complete.
                        </p>
                      </div>
                    ) : null}
                    <div className="rounded-lg border bg-white p-4">
                      <div className="flex items-center gap-2 text-navy-900">
                        {isLandlord ? <ShieldCheck className="h-4 w-4 text-amber-600" /> : <Mail className="h-4 w-4 text-amber-600" />}
                        <p className="text-sm font-medium">{isLandlord ? 'Account security' : 'Contact readiness'}</p>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        {isLandlord
                          ? 'Keep your password and login email current to avoid access issues.'
                          : profileForm.phone?.trim()
                            ? 'Your contact details are ready and up to date.'
                            : 'Add a phone number if you want faster contact from the portal team.'}
                      </p>
                    </div>
                  </div>
                  {isLandlord && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-4">
                      <div className="flex items-start gap-3">
                        <Receipt className="h-5 w-5 text-emerald-700 mt-0.5" />
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-emerald-900">Rent receipt profile</p>
                            <Badge variant="outline" className="border-emerald-300 bg-white/80 text-emerald-800">
                              {receiptSetupPercent === 100 ? 'Ready to send' : 'Needs review'}
                            </Badge>
                          </div>
                          <p className="mt-1 text-sm text-emerald-900/90">
                            {receiptSetupCompleteCount} of {receiptSetupTotalCount} recommended receipt details are ready.
                          </p>
                          <p className="mt-1 text-xs text-emerald-800">
                            Review these details before marking rent as paid so the generated receipt shows the right issuer information.
                          </p>
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-emerald-800/90">
                              <span>Setup progress</span>
                              <span>{receiptSetupPercent}%</span>
                            </div>
                            <Progress value={receiptSetupPercent} className="mt-2 h-2 bg-emerald-100" />
                          </div>
                          {receiptMissingItems.length > 0 ? (
                            <div className="mt-3 rounded-lg border border-emerald-200 bg-white/80 p-3">
                              <p className="text-xs font-medium text-emerald-900">To reach 100%, add:</p>
                              <ul className="mt-2 space-y-2 text-xs text-emerald-900/90">
                                {receiptMissingItems.map((item) => (
                                  <li key={item.label} className="flex flex-wrap items-center justify-between gap-2">
                                    <span>{item.label}</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${item.required ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}`}>
                                      {item.required ? 'Required' : 'Recommended'}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="mt-3 text-xs font-medium text-emerald-900">
                              Everything in the current receipt checklist is complete.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {isNonLandlord && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4">
                      <div className="flex items-start gap-3">
                        <User className="mt-0.5 h-5 w-5 text-blue-700" />
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-blue-900">Profile completeness</p>
                            <Badge variant="outline" className="border-blue-300 bg-white/80 text-blue-800">
                              {nonLandlordPercent === 100 ? 'Ready' : 'Needs review'}
                            </Badge>
                          </div>
                          <p className="mt-1 text-sm text-blue-900/90">
                            {nonLandlordCompleteCount} of {nonLandlordTotalCount} key account details are ready.
                          </p>
                          <p className="mt-1 text-xs text-blue-800">
                            Keep your profile details current so your account information and contact details stay accurate.
                          </p>
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wide text-blue-800/90">
                              <span>Setup progress</span>
                              <span>{nonLandlordPercent}%</span>
                            </div>
                            <Progress value={nonLandlordPercent} className="mt-2 h-2 bg-blue-100" />
                          </div>
                          {nonLandlordMissingItems.length > 0 ? (
                            <div className="mt-3 rounded-lg border border-blue-200 bg-white/80 p-3">
                              <p className="text-xs font-medium text-blue-900">To reach 100%, add:</p>
                              <ul className="mt-2 space-y-2 text-xs text-blue-900/90">
                                {nonLandlordMissingItems.map((item) => (
                                  <li key={item.label} className="flex flex-wrap items-center justify-between gap-2">
                                    <span>{item.label}</span>
                                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${item.required ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-700'}`}>
                                      {item.required ? 'Required' : 'Recommended'}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="mt-3 text-xs font-medium text-blue-900">
                              Everything in the current profile checklist is complete.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium text-navy-900">Quick actions</p>
                  {isLandlord && (
                    <Button type="button" variant="outline" className="w-full justify-between" onClick={() => navigateToProfileView('profile', 'receipt-settings')}>
                      Review rent receipt details
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                  {isNonLandlord && (
                    <Button type="button" variant="outline" className="w-full justify-between" onClick={() => navigateToProfileView('profile', 'personal-details')}>
                      Review profile details
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                  <Button type="button" variant="outline" className="w-full justify-between" onClick={() => navigateToProfileView('email')}>
                    Change email address
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" className="w-full justify-between" onClick={() => navigateToProfileView('security')}>
                    Update password
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  {/* // TODO: reinstate when billing is active
                  <Button type="button" variant="outline" className="w-full justify-between" onClick={() => navigate('/settings/billing')}>
                    View billing preview
                    <ArrowRight className="h-4 w-4" />
                  </Button> */}
                </div>
              </div>
            </CardContent>
          </Card>

          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6 sm:space-y-8">
            <TabsList className="grid h-auto w-full max-w-lg grid-cols-3 gap-1">
              <TabsTrigger value="profile" data-testid="profile-tab" className="px-2 py-2 text-xs sm:text-sm">
                <User className="h-4 w-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="security-tab" className="px-2 py-2 text-xs sm:text-sm">
                <Lock className="h-4 w-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="email-tab" className="px-2 py-2 text-xs sm:text-sm">
                <Mail className="h-4 w-4 mr-2" />
                Email
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <Card>
                <CardHeader>
                  <CardTitle>Profile settings</CardTitle>
                  <CardDescription>Edit the details you want the portal to use across your account.</CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleProfileUpdate} className="space-y-6">
                    <div className="rounded-lg border bg-muted/20 p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <p className="text-sm font-medium text-navy-900">Manage by task</p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            The overview above already shows your account summary. Use these shortcuts to jump straight to the fields you want to edit.
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {isLandlord ? (
                            <Button type="button" variant="outline" size="sm" onClick={() => navigateToProfileView('profile', 'receipt-settings')}>
                              Receipt details
                            </Button>
                          ) : (
                            <Button type="button" variant="outline" size="sm" onClick={() => navigateToProfileView('profile', 'personal-details')}>
                              Personal details
                            </Button>
                          )}
                          {isLandlord ? null : (
                            <Button type="button" variant="outline" size="sm" onClick={() => navigateToProfileView('profile', 'contact-details')}>
                              Contact details
                            </Button>
                          )}
                          {isLandlord ? (
                            <Button type="button" variant="outline" size="sm" onClick={() => navigateToProfileView('profile', 'contact-branding')}>
                              Contact & branding
                            </Button>
                          ) : null}
                          <Button type="button" variant="outline" size="sm" onClick={() => navigateToProfileView('email')}>
                            Email
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => navigateToProfileView('security')}>
                            Password
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* ===== ISSUER INFORMATION (Landlords only) ===== */}
                    {user?.role === 'landlord' && (
                      <Card id="receipt-settings-section" className={`bg-muted/30 transition-shadow ${receiptSectionHighlighted ? 'ring-2 ring-emerald-400 ring-offset-2' : ''}`}>
                        <CardContent className="pt-6 space-y-6">
                          <div className="pb-2 border-b">
                            <h3 className="text-sm font-semibold">Receipt issuer details</h3>
                            <p className="text-xs text-muted-foreground mt-1">{isCompanyLandlord ? 'Company issuer fields apply — fill in your registered company details.' : 'Individual landlord issuer fields apply — use your own name and correspondence address.'}</p>
                          </div>

                          {!isCompanyLandlord && (
                            <div className="pt-4 space-y-4 border-t">
                              <div className="pb-2 border-b">
                                <h4 className="text-sm font-semibold">Individual landlord details</h4>
                                <p className="mt-1 text-xs text-muted-foreground">Use your own name and correspondence details as the issuer shown on receipts.</p>
                              </div>
                              <div>
                                <Label>Landlord Full Name</Label>
                                <Input
                                  value={profileForm.name}
                                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                                  placeholder="Full Name (e.g. John Smith)"
                                  data-testid="profile-name-input"
                                />
                              </div>
                              <div>
                                <Label>Correspondence address (Optional)</Label>
                                <AddressInput
                                  defaultValue={profileForm.business_address}
                                  onAddressSelect={handleBusinessAddressSelect}
                                  placeholder="Address to show on receipts"
                                />
                              </div>
                            </div>
                          )}

                          {isCompanyLandlord && (
                            <div className="pt-4 space-y-4 border-t">
                              <div className="pb-2 border-b">
                                <h4 className="text-sm font-semibold">Company landlord details</h4>
                                <p className="mt-1 text-xs text-muted-foreground">Use your registered company details for receipt issuer information.</p>
                              </div>
                              <div>
                                <Label>Company name</Label>
                                <Input
                                  value={profileForm.company_name}
                                  onChange={(e) => {
                                    setProfileForm({ ...profileForm, company_name: e.target.value });
                                    setProfileFieldErrors((prev) => ({ ...prev, company_name: '' }));
                                  }}
                                  placeholder="Your registered company name"
                                  data-testid="profile-company-name-input"
                                />
                                {profileFieldErrors.company_name ? (
                                  <p className="mt-1 text-xs text-destructive">{profileFieldErrors.company_name}</p>
                                ) : null}
                              </div>
                              <div>
                                <Label>Business address (Optional)</Label>
                                <AddressInput
                                  defaultValue={profileForm.business_address}
                                  onAddressSelect={handleBusinessAddressSelect}
                                  placeholder="Address to show on receipts"
                                />
                              </div>
                              <div>
                                <Label>Primary contact Full Name</Label>
                                <Input
                                  value={profileForm.name}
                                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                                  placeholder="Full Name (e.g. John Smith)"
                                  data-testid="profile-name-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Used across the portal for the account holder. Receipts use the company name above as the main issuer.
                                </p>
                              </div>
                              <div>
                                <Label id="company-year-end-label">Company financial year end</Label>
                                <Select
                                  value={normaliseYearEnd(profileForm.company_year_end)}
                                  onValueChange={(val) => setProfileForm({ ...profileForm, company_year_end: val })}
                                >
                                  <SelectTrigger
                                    aria-labelledby="company-year-end-label"
                                    data-testid="company-year-end-select"
                                  >
                                    <SelectValue placeholder="Select financial year end" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {COMPANY_YEAR_END_OPTIONS.map(({ value, label }) => (
                                      <SelectItem key={value} value={value}>{label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Select the month your accounting period ends. Used for CT600 summaries and reporting periods. Consult your accountant if unsure.
                                </p>
                              </div>
                              <div>
                                <Label>Director or authorised signatory Full Name (Optional)</Label>
                                <Input
                                  value={profileForm.director_name}
                                  onChange={(e) => setProfileForm({ ...profileForm, director_name: e.target.value })}
                                  placeholder="Full Name (e.g. John Smith)"
                                  data-testid="director-name-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Used on receipts and tenancy agreements to identify the authorised signatory.
                                </p>
                              </div>
                              <div>
                                <Label>Company Registration (Optional)</Label>
                                <Input
                                  value={profileForm.company_registration}
                                  onChange={(e) => setProfileForm({ ...profileForm, company_registration: e.target.value })}
                                  placeholder="Company registration number"
                                  data-testid="profile-company-registration-input"
                                />
                              </div>
                              <div>
                                <Label>VAT Number (Optional)</Label>
                                <Input
                                  value={profileForm.vat_number}
                                  onChange={(e) => setProfileForm({ ...profileForm, vat_number: e.target.value })}
                                  placeholder="VAT number"
                                  data-testid="profile-vat-number-input"
                                />
                              </div>
                            </div>
                          )}

                          <div id="contact-branding-section" className={`rounded-xl border border-transparent pt-4 transition-shadow ${getSectionHighlightClass('contact-branding')}`}>
                            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-navy-900">Receipt contact & branding</p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Add the remaining contact details and logo tenants should see on generated receipts.
                                </p>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <ImageUp className="h-4 w-4" />
                                Optional but recommended for a more complete receipt.
                              </div>
                            </div>
                            <div className="space-y-4">
                            <div>
                              <Label>Phone (Optional)</Label>
                              <Input
                                value={profileForm.phone}
                                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                                placeholder="Contact phone number"
                                data-testid="profile-phone-input"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Shown on receipts if you want tenants to have a direct contact number.
                              </p>
                            </div>
                            <div>
                              <Label>Website (Optional)</Label>
                              <Input
                                value={profileForm.website}
                                onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                                placeholder="https://yourwebsite.com"
                                data-testid="profile-website-input"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Useful for landlords who want receipts to include a website or portfolio link.
                              </p>
                            </div>
                            <div>
                              <Label>{isCompanyLandlord ? 'Company logo (PNG/JPG, max 2MB)' : 'Logo or brand mark (PNG/JPG, max 2MB)'}</Label>
                              <div className="mt-2 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                                {currentLogoUrl && !logoImageFailed ? (
                                  <img
                                    src={currentLogoUrl}
                                    alt="Receipt logo"
                                    className="h-14 w-auto max-w-full rounded border border-muted"
                                    onError={() => setLogoImageFailed(true)}
                                  />
                                ) : (
                                  <div className="flex h-20 w-full max-w-40 flex-col items-center justify-center rounded-lg border border-dashed border-muted-foreground/40 bg-muted/30 px-3 text-center">
                                    <p className="text-xs font-medium text-navy-900">No logo uploaded</p>
                                    <p className="mt-1 text-[11px] leading-snug text-muted-foreground">
                                      Add a PNG or JPG if you want a logo or brand mark on receipts.
                                    </p>
                                  </div>
                                )}
                                <div className="w-full space-y-2">
                                  <Input
                                    ref={logoInputRef}
                                    type="file"
                                    accept="image/png,image/jpeg"
                                    onChange={handleLogoInputChange}
                                    className="sr-only"
                                    data-testid="profile-logo-input"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="w-full justify-center text-sm"
                                    onClick={() => logoInputRef.current?.click()}
                                  >
                                    Choose logo file
                                  </Button>
                                  <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                                    {logoFile ? `${logoFile.name} selected — save changes to upload` : 'PNG or JPG, up to 2MB'}
                                  </div>
                                </div>
                              </div>
                              <p className="text-[11px] text-muted-foreground">
                                We'll crop your logo to the receipt header ratio and upload it when you save changes.
                              </p>
                            </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* ===== NON-LANDLORD PROFILE ===== */}
                    {user?.role !== 'landlord' && (
                      <div className="space-y-6">
                        <div id="personal-details-section" className={`space-y-4 rounded-xl border border-transparent bg-white/70 p-4 transition-shadow ${getSectionHighlightClass('personal-details')}`}>
                          <div className="pb-2 border-b">
                            <h3 className="text-sm font-semibold">Personal details</h3>
                            <p className="mt-1 text-xs text-muted-foreground">Your name is shown across the portal wherever your account is referenced.</p>
                          </div>
                          <div>
                            <Label>Full Name</Label>
                            <Input
                              value={profileForm.name}
                              onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                              placeholder="Full Name (e.g. John Smith)"
                              data-testid="profile-name-input"
                            />
                          </div>
                        </div>

                        <div id="contact-details-section" className={`space-y-4 rounded-xl border border-transparent bg-white/70 p-4 transition-shadow ${getSectionHighlightClass('contact-details')}`}>
                          <div className="pb-2 border-b">
                            <h3 className="text-sm font-semibold">Contact details</h3>
                            <p className="mt-1 text-xs text-muted-foreground">Add optional contact details if you want a fuller account profile.</p>
                          </div>
                          <div>
                            <Label>Phone (Optional)</Label>
                            <Input
                              value={profileForm.phone}
                              onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                              placeholder="Your phone number"
                              data-testid="profile-phone-input"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <Button type="submit" disabled={loading} data-testid="save-profile-btn" className="w-full sm:w-auto">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      {logoFile ? 'Save Changes & Upload Logo' : 'Save Changes'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePasswordChange} className="space-y-4">
                    <div>
                      <Label>Current Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                        placeholder="Enter current password"
                        required
                        data-testid="current-password-input"
                      />
                    </div>
                    <div>
                      <Label>New Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        placeholder="Enter new password (min 6 characters)"
                        required
                        data-testid="new-password-input"
                      />
                    </div>
                    <div>
                      <Label>Confirm New Password</Label>
                      <Input
                        type="password"
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                        placeholder="Confirm new password"
                        required
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="change-password-btn">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                      Change Password
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="email">
              <Card>
                <CardHeader>
                  <CardTitle>Change Email Address</CardTitle>
                  <CardDescription>A verification link will be sent to your new email</CardDescription>
                </CardHeader>
                <CardContent>
                  {emailSent ? (
                    <div className="text-center py-6 space-y-3" data-testid="email-sent-confirmation">
                      <CheckCircle className="h-12 w-12 text-green-600 mx-auto" />
                      <p className="font-medium text-green-700">Verification email sent!</p>
                      <p className="text-sm text-muted-foreground">
                        Check your inbox at <strong>{emailForm.new_email}</strong> and click the verification link.
                        The link expires in 1 hour.
                      </p>
                      <Button variant="outline" onClick={() => setEmailSent(false)} data-testid="send-another-email-btn">
                        Send to a different address
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleEmailChange} className="space-y-4">
                      <div>
                        <Label>Current Email</Label>
                        <Input value={user?.email || ''} disabled className="bg-muted" />
                      </div>
                      <div>
                        <Label>New Email Address</Label>
                        <Input
                          type="email"
                          value={emailForm.new_email}
                          onChange={(e) => setEmailForm({ new_email: e.target.value })}
                          placeholder="Enter new email address"
                          required
                          data-testid="new-email-input"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        We'll send a verification link to this address. You'll need to log in again after the change.
                      </p>
                      <Button type="submit" disabled={loading} data-testid="request-email-change-btn">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                        Send Verification Email
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <Card className="mt-8 border-red-200 bg-red-50/60 shadow-sm" data-testid="close-account-danger-zone">
            <CardHeader>
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-700" />
                <div>
                  <CardTitle className="text-red-950">Danger zone</CardTitle>
                  <CardDescription className="text-red-900/80">
                    Close your account only if you no longer need portal access.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-red-950">
                Closing your account removes your ability to log in. Historical records tied to tenancy, rent,
                compliance, audit obligations, documents, messages, and applications may still be retained.
              </p>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setCloseAccountDialogOpen(true)}
                data-testid="close-account-btn"
              >
                Close account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={closeAccountDialogOpen} onOpenChange={(open) => { if (!closeAccountLoading) setCloseAccountDialogOpen(open); }}>
        <DialogContent hideCloseButton={closeAccountLoading}>
          <DialogHeader>
            <DialogTitle>Close account?</DialogTitle>
            <DialogDescription>
              This will remove your portal access. It does not erase historical records tied to tenancy, rent,
              compliance, audit obligations, documents, messages, or applications.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-950">
            Your account will be deactivated and you will be signed out. If you have active landlord responsibilities,
            support may need to help close or transfer them first.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCloseAccountDialogOpen(false)} disabled={closeAccountLoading}>
              Keep account
            </Button>
            <Button type="button" variant="destructive" onClick={handleCloseAccount} disabled={closeAccountLoading} data-testid="confirm-close-account-btn">
              {closeAccountLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Close account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cropDialogOpen} onOpenChange={(open) => { if (!open && !cropPreparing) resetCropState(); }}>
        <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Crop logo for receipts</DialogTitle>
            <DialogDescription>
              Use the wide frame shown on receipts. Drag to reposition and zoom until the logo sits comfortably inside the header area.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="rounded-xl border bg-muted/20 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Crop className="h-4 w-4" />
                  Receipt frame: 22:7 wide header
                </div>
                <div className="flex items-center gap-2">
                  <Move className="h-4 w-4" />
                  Drag to position
                </div>
              </div>

              <div className="flex justify-center">
                <div
                  className="relative overflow-hidden rounded-lg border bg-white shadow-sm touch-none select-none"
                  style={{ width: `${cropPreviewWidth}px`, height: `${cropPreviewHeight}px`, cursor: cropSourceUrl ? 'grab' : 'default' }}
                  onPointerDown={handleCropPointerDown}
                  onPointerMove={handleCropPointerMove}
                  onPointerUp={handleCropPointerUp}
                  onPointerCancel={handleCropPointerUp}
                >
                  {cropSourceUrl ? (
                    <img
                      src={cropSourceUrl}
                      alt="Crop preview"
                      className="absolute left-1/2 top-1/2 max-w-none"
                      style={{
                        width: `${cropDisplaySize.width}px`,
                        height: `${cropDisplaySize.height}px`,
                        transform: `translate(calc(-50% + ${cropOffset.x}px), calc(-50% + ${cropOffset.y}px))`,
                      }}
                      draggable={false}
                    />
                  ) : null}
                  <div className="pointer-events-none absolute inset-0 border-2 border-white/90 shadow-[inset_0_0_0_9999px_rgba(15,23,42,0.22)]" />
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-navy-900">
                    <ZoomIn className="h-4 w-4" />
                    Zoom
                  </div>
                  <span className="text-muted-foreground">{cropZoom.toFixed(1)}x</span>
                </div>
                <Slider
                  value={[cropZoom]}
                  min={0.5}
                  max={3}
                  step={0.1}
                  onValueChange={(value) => setCropZoom(value[0] || 1)}
                />
                <p className="text-xs text-muted-foreground">
                  Zoom out to keep extra white space around the logo on the receipt.
                </p>
              </div>
              <Button type="button" variant="outline" onClick={() => { setCropZoom(1); setCropOffset({ x: 0, y: 0 }); }} disabled={cropPreparing}>
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={resetCropState} disabled={cropPreparing}>Cancel</Button>
            <Button type="button" onClick={handleConfirmCrop} disabled={cropPreparing || !cropSourceUrl}>
              {cropPreparing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Use cropped logo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
